/**
 * Visible "新版本来啦，点这里更新" banner for the PWA.
 *
 * Registers/looks up the service worker, watches for a waiting worker
 * (= a deploy that is ready to take over), and shows a fixed banner the
 * user can tap to apply the update. We deliberately do NOT auto-skip in
 * the SW (see `public/sw.js`) so a child mid-quiz isn't yanked into a
 * reload — they only update when they tap.
 */
import { useEffect, useState } from 'react';
import { t, useLang } from '../i18n';

const POLL_MS = 60_000; // poll for SW updates every minute while open

export default function UpdateBanner() {
  useLang();
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    let cancelled = false;
    let pollTimer: number | null = null;

    function attachWaiting(reg: ServiceWorkerRegistration) {
      if (reg.waiting && navigator.serviceWorker.controller) {
        setWaitingWorker(reg.waiting);
      }
      reg.addEventListener('updatefound', () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            setWaitingWorker(installing);
          }
        });
      });
    }

    navigator.serviceWorker.getRegistration().then((reg) => {
      if (cancelled || !reg) return;
      attachWaiting(reg);
      // Periodic check for new SW so a long-lived PWA tab notices deploys.
      pollTimer = window.setInterval(() => {
        reg.update().catch(() => { /* offline — try again later */ });
      }, POLL_MS);
    });

    // When the new SW takes control after SKIP_WAITING, reload once.
    let reloaded = false;
    const onCtrlChange = () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onCtrlChange);

    return () => {
      cancelled = true;
      if (pollTimer != null) window.clearInterval(pollTimer);
      navigator.serviceWorker.removeEventListener('controllerchange', onCtrlChange);
    };
  }, []);

  function applyUpdate() {
    if (!waitingWorker) return;
    setReloading(true);
    // Hint to App.tsx's boot path that the very next loadMe() failure
    // (if any) is almost certainly a transient post-update race, NOT
    // a real auth problem — so it should retry instead of clearing the
    // session. Cleared after the boot path consumes it.
    try { sessionStorage.setItem('xpb.justUpdated', String(Date.now())); }
    catch { /* private mode etc — ignore */ }
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    // Safety net: if controllerchange doesn't fire within 3s, force reload.
    window.setTimeout(() => window.location.reload(), 3000);
  }

  if (!waitingWorker) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: 12, right: 12, bottom: 12,
        zIndex: 9999,
        background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
        color: '#fff',
        borderRadius: 16,
        padding: '12px 16px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontWeight: 700,
      }}
    >
      <span style={{ fontSize: 24 }}>✨</span>
      <div style={{ flex: 1, lineHeight: 1.3 }}>
        <div style={{ fontSize: 15 }}>{t('update.banner.title')}</div>
        <div style={{ fontSize: 12, opacity: 0.9, fontWeight: 500 }}>
          {t('update.banner.body')}
        </div>
      </div>
      <button
        onClick={applyUpdate}
        disabled={reloading}
        style={{
          background: '#fff',
          color: '#dc2626',
          border: 'none',
          padding: '10px 18px',
          borderRadius: 12,
          fontWeight: 800,
          fontSize: 15,
          cursor: 'pointer',
          minWidth: 88,
        }}
      >
        {reloading ? t('home.update.reloading') : t('update.banner.button')}
      </button>
    </div>
  );
}
